import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import Select from '@/common/components/Select'
import { filterOptions } from '@/common/constants/filterOptions.constant'
import useDebounce from '@/common/hooks/useDebounce'
import { Option } from '@/common/types/option'
import { cn } from '@/common/utils/cn'
import { formatCurrency } from '@/common/utils/formatters'
import { unFormatCurrency } from '@/common/utils/unFormatters'
import { useClientCompanyBranchAvailableToAllocateSummary } from '@/features/companyBranches/hooks/useClientCompanyBranchAvailableToAllocateSummary'
import { ClientCompanyAllowedCategoriesAndProductsTransformer } from '@/features/companyBranches/services/allowedCategoriesAndProductsTransformer'
import { ClientCompanyAllowedCategoriesAndProducts, ClientCompanyAllowedCategoryAndProductData } from '@/features/companyBranches/types/allowedCategoriesAndProducts.type'
import { EntryProductFormItem } from '@/features/governmentAllocations/pages/Update/components/EntryProductFormItem'
import { GovernmentAllocationEntryProductCategoryValidationSchema } from '@/features/governmentAllocations/schemas/update.schema'
import {
  GovernmentAllocationEntryProduct,
  GovernmentAllocationEntryProductCategory,
} from '@/features/governmentAllocations/types/governmentAllocationEntry.type'
import { BoxesIcon, SearchIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { FilterOptionOption } from 'react-select'

interface EntryProductCategoryProps {
  companyBranchId?: string
  entryProductCategory: GovernmentAllocationEntryProductCategory
  allowedCategorySummary: ClientCompanyAllowedCategoryAndProductData
  disabled: boolean
  onTotalValueChange?: () => void
}

export const EntryProductCategory = ({
  companyBranchId,
  entryProductCategory,
  allowedCategorySummary,
  disabled,
  onTotalValueChange,
}: EntryProductCategoryProps) => {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext<GovernmentAllocationEntryProductCategoryValidationSchema>()

  const [searchProduct, setSearchProduct] = useDebounce('')

  const {
    data: allowedCategoriesAndProducts,
    isPending: isAllowedCategoriesAndProductsPending,
  } = useClientCompanyBranchAvailableToAllocateSummary<ClientCompanyAllowedCategoriesAndProducts>({
    companyBranchId,
    filters: {
      search: searchProduct,
      productCategoryId: entryProductCategory?.product_category?.id,
    },
  });

  const allowedProductsOptions = useMemo(() => {
    return isAllowedCategoriesAndProductsPending || !allowedCategoriesAndProducts || !allowedCategoriesAndProducts['products']
      ? []
      : ClientCompanyAllowedCategoriesAndProductsTransformer.optionsTransformer({ key: 'products' })(allowedCategoriesAndProducts || {})
  }, [isAllowedCategoriesAndProductsPending]);

  const {
    fields: entryProducts,
    append: appendEntryProduct,
    remove: removeEntryProduct,
  } = useFieldArray({
    control,
    name: 'entry_products',
  })

  const watchCurrentCategoryGrantedCredit = useWatch({
    control,
    name: 'total_value',
  });

  const availableTotal = Number(allowedCategorySummary?.available_total) || 0;
  const availableForAllocation = Math.max(availableTotal - unFormatCurrency(watchCurrentCategoryGrantedCredit), 0);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between">
        <div className="flex items-center gap-x-2">
          <BoxesIcon strokeWidth={1} />

          <p>{entryProductCategory?.product_category?.display_name}</p>
        </div>
      </div>

      <div className='flex flex-col gap-1'>
        <CurrencyTextField
          label="Valor total do serviço"
          {...register('total_value')}
          onChange={(event) => {
            register('total_value').onChange(event)
            onTotalValueChange?.()
          }}
          markAsRequired
          forceValue
          value={watch('total_value')}
          errorMessage={errors.total_value?.message || undefined}
          disabled={disabled || entryProducts?.length > 0}
        />

        {allowedCategorySummary && (
          <div className='flex gap-3'>
            <p className='italic text-xs text-white-400'>
              Total disponível: {formatCurrency(Number(allowedCategorySummary?.available_total) || 0)}
            </p>

            <p className='italic text-xs text-white-400'>
              Disponível para distribuir: {formatCurrency(availableForAllocation)}
            </p>

            <p className='italic text-xs text-white-400'>
              Total garantido: {formatCurrency(Number(allowedCategorySummary?.granted_total) || 0)}
            </p>

            <p className='italic text-xs text-white-400'>
              Total inserido: {formatCurrency(Number(allowedCategorySummary?.entry_total) || 0)}
            </p>
          </div>
        )}
      </div>

      <Box className="space-y-4 p-4">
        <Select
          placeholder={
            <div className={cn('flex items-center gap-x-2', !disabled && 'text-white-800')}>
              <SearchIcon />
              <p>Pesquisar produtos</p>
            </div>
          }
          isLoading={isAllowedCategoriesAndProductsPending}
          options={
            allowedProductsOptions?.filter((productOption) => {
              return !entryProducts?.some(({ product }) => product?.id === productOption.value)
            }) || []
          }
          onInputChange={setSearchProduct}
          filterOption={filterOptions as unknown as (option: FilterOptionOption<unknown>, inputValue: string) => boolean}
          value=""
          onChange={(newValue) => {
            appendEntryProduct({
              product: {
                id: (newValue as Option).value,
                display_name: (newValue as ClientCompanyAllowedCategoryAndProductData).display_name,
                unit_for_quantity: (newValue as ClientCompanyAllowedCategoryAndProductData).unit_for_quantity,
                category: {
                  id: '',
                  display_name: '',
                  products_count: ''
                }
              },
              total_value: '',
            })
            onTotalValueChange?.()
          }}
          isDisabled={disabled}
        />

        {entryProducts?.map((entryProduct, index) => (
          <EntryProductFormItem
            key={entryProduct?.product?.id}
            entryProductIndex={index}
            entryProduct={entryProduct as GovernmentAllocationEntryProduct}
            onDelete={() => {
              removeEntryProduct(index)
              onTotalValueChange?.()
            }}
            disabled={disabled}
            allowedProductSummary={allowedCategoriesAndProducts?.products?.[entryProduct?.product?.id]}
            onTotalValueChange={onTotalValueChange}
          />
        ))}
      </Box>
    </div>
  )
}
